import { useState, useEffect, useRef } from "react";
import { cn } from "@githits/shared";
import type { ReplaySession } from "../data/types";
import LanguageIcon from "./LanguageIcon";

const LANGUAGE_LABELS: Record<string, string> = {
  typescript: "TypeScript",
  python: "Python",
  rust: "Rust",
  cuda: "CUDA",
  go: "Go",
  elixir: "Elixir",
  java: "Java",
  csharp: "C#",
};

const PAGE_SIZE = 4;
const VISIBLE_MS = 5000;
const TRANSITION_MS = 400;

interface QuestionSelectorProps {
  sessions: Record<string, ReplaySession>;
  activeKey: string | null;
  onSelect: (key: string) => void;
}

export default function QuestionSelector({
  sessions,
  activeKey,
  onSelect,
}: QuestionSelectorProps) {
  const entries = Object.entries(sessions);
  const numPages = Math.ceil(entries.length / PAGE_SIZE);

  const [page, setPage] = useState(0);
  const [animState, setAnimState] = useState<"idle" | "exiting" | "entering">("idle");
  const isPausedRef = useRef(false);

  useEffect(() => {
    if (numPages <= 1) return;

    let t1: ReturnType<typeof setTimeout>;
    let t2: ReturnType<typeof setTimeout>;

    const cycle = () => {
      if (isPausedRef.current) return;
      setAnimState("exiting");
      t1 = setTimeout(() => {
        setPage((p) => (p + 1) % numPages);
        setAnimState("entering");
        t2 = setTimeout(() => setAnimState("idle"), TRANSITION_MS);
      }, TRANSITION_MS);
    };

    const interval = setInterval(cycle, VISIBLE_MS + TRANSITION_MS * 2);
    return () => {
      clearInterval(interval);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [numPages]);

  const pageEntries = entries.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <nav
      className="ghw-flex ghw-flex-col ghw-items-start ghw-gap-2"
      aria-label="Replay demo questions"
    >
      <div
        onMouseEnter={() => { isPausedRef.current = true; }}
        onMouseLeave={() => { isPausedRef.current = false; }}
        className={cn(
          "ghw-flex ghw-flex-col ghw-items-start ghw-gap-2 ghw-w-full",
          animState === "exiting" && "animate-replay-selector-exit",
          animState === "entering" && "animate-replay-selector-enter"
        )}
      >
        {pageEntries.map(([key, session]) => {
          const isActive = key === activeKey;
          const label = LANGUAGE_LABELS[key] ?? key;
          return (
            <button
              key={key}
              aria-pressed={isActive}
              onClick={() => onSelect(key)}
              className={cn(
                "ghw-flex ghw-items-center ghw-gap-2 ghw-p-3 ghw-rounded-lg ghw-text-sm ghw-font-medium",
                "ghw-max-w-full ghw-transition-colors ghw-cursor-pointer",
                "ghw-bg-secondary ghw-text-secondary-foreground hover:ghw-bg-secondary/80",
                "focus-visible:ghw-outline-none focus-visible:ghw-ring-2 focus-visible:ghw-ring-primary/40",
                isActive && "ghw-border-b-2 ghw-border-primary ghw-text-primary"
              )}
            >
              <LanguageIcon language={key} label={label} isActive={isActive} />
              <span className="ghw-truncate">
                {session.query.replace(/\s+/g, " ").trim()}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
